import { Controller } from "@hotwired/stimulus"
import { useClickOutside } from "stimulus-use"
import debounce from "lodash/debounce"

/* Module-level cache of the last decision, preserved across controller
 * re-instantiations (the controller is recreated on every Turbo Drive
 * navigation, because the top bar is part of the body that gets swapped).
 *
 * The decision (expanded vs. collapsed) depends only on viewport and menu
 * widths. The menu width is deterministic for a given user session, so as
 * long as the viewport width hasn't changed we can apply the previous
 * decision synchronously and skip the measurement dance — which is what
 * is causing the top bar to flicker on every navigation. */
let cachedDecision = null

/**
 * Responsive menu controller
 * - allows you to dynamically show/hide the menu display/hide button based
 *   on the menu width and viewport width
 * - allows to open and close a drop-down menu on a button click.
 * Drop down menu can be closed on clicking outside as well.
 *
 *  %nav.invisible{data: { controller: 'responsive-menu',
 *                         responsive_menu_expanded_class: 'expanded',
 *                         responsive_menu_collapsed_class: 'collapsed',
 *                         responsive_menu_hidden_class: 'hidden!',
 *                         responsive_menu_invisible_class: 'invisible',
 *                         responsive_menu_invisible_class: 'invisible',
 *                         turbo_prefetch: 'false', }}
 *
 *    %ul{data: { responsive_menu_target: 'content' }}
 *      %li.main-menu-item Menu item 1
 *      %li.main-menu-item Menu item 2
 *      %li.main-menu-item Menu item 3
 *
 *    %button.block.ml-auto.mr-0{aria: { expanded: 'false', label: 'Menu' },
 *                               data: { action: 'click->responsive-menu#toggle', responsive_menu_target: 'toggler' }
 *
 * If the menu layout contains a logo and a dropdown menu icon, you need to mark
 * them as targets for this controller:
 *   responsive_menu_target: 'dropdown'
 *   responsive_menu_target: 'logo'
 *
 * We need their dimensions to calculate the width of the responsive menu itself.
 */

export default class extends Controller {
  static targets = ["content", "toggler", "logo", "dropdown", "menuLogoContainer"]
  static classes = ["expanded", "collapsed", "hidden", "invisible"]

  togglerHeight = 0

  async connect() {
    useClickOutside(this)

    this.clickOutsideListener = this._clickOutsideListener.bind(this)

    this.debouncedResizeListener = debounce(this._resizeListener.bind(this), 100)
    window.addEventListener("resize", this.debouncedResizeListener)

    this.monitorTableChange = this.monitorTable.bind(this)
    window.addEventListener("table-sticky-header:change", this.monitorTableChange)

    // We need to lister for turbo render because the menu is `invisible` by default (to avoid flickering).
    // When form validation fails, the menu is rendered again, but the controller isn't reconnected automatically,
    // so we need to listen for the render event manually
    this.turboRenderListener = this._turboRenderListener.bind(this)
    document.addEventListener("turbo:render", this.turboRenderListener)

    this._menuStatePromise = Promise.resolve()

    // Synchronously apply the previously computed state so the freshly inserted
    // top bar paints with the correct expanded/collapsed classes from the very
    // first frame. Falls back to the full measurement on first load or after
    // a viewport resize when no usable cached decision exists.
    if (this._applyCachedDecision()) return

    await this._loadListener()
  }

  disconnect() {
    super.disconnect()
    this.debouncedResizeListener.cancel()
    window.removeEventListener("resize", this.debouncedResizeListener)
    window.removeEventListener("table-sticky-header:change", this.monitorTableChange)
    document.removeEventListener("turbo:render", this.turboRenderListener)
  }

  monitorTable() {
    this._menuStatePromise = this._menuStatePromise
      .then(() => {
        if (this._applyCachedDecision()) return
        return this._setMenuState()
      })
      .catch((err) => {
        console.error("ResponsiveMenuController: _setMenuState failed in monitorTable", err)
      })
  }

  toggle(event) {
    if (event.currentTarget.ariaExpanded === "true") {
      this._removeClickOutsideListener()
      this._hideMenu()
    } else {
      this._addClickOutsideListener()
      this._showMenu()
    }
  }

  async _resizeListener() {
    /* Viewport width changes invalidate the cached decision because the
     * collapsed/expanded choice depends on it. */
    cachedDecision = null
    await this._setMenuState()
  }

  async _loadListener() {
    if (document.readyState === "loading") {
      document.addEventListener(
        "DOMContentLoaded",
        async () => {
          await this._setMenuState()
        },
        { once: true },
      )
    } else {
      await this._setMenuState()
    }
  }

  _clickOutsideListener(event) {
    if (!event.target.closest('[data-responsive-menu-target="toggler"]')) {
      this._hideMenu()
    }
  }

  async _turboRenderListener() {
    if (this._applyCachedDecision()) return
    await this._setMenuState()
  }

  /* Apply the previously computed expanded/collapsed state synchronously
   * (no requestAnimationFrame, no measurement, no `invisible` flash).
   * Returns true when the cache was applied, false when a full measurement
   * is still required (no cache yet or viewport width has changed). */
  _applyCachedDecision() {
    if (!cachedDecision) return false
    if (cachedDecision.viewportWidth !== window.innerWidth) return false

    this.togglerHeight = cachedDecision.togglerHeight

    if (cachedDecision.state === "collapsed") {
      this._collapseMenu()
      this._showToggler()
      this._hideMenu()
    } else {
      this._expandMenu()
      this._hideToggler()
      this._showMenu()
      this._removeClickOutsideListener()
    }
    this._setMenuLogoContainerClass(cachedDecision.state)
    this.contentTarget.classList.remove(this.invisibleClass)
    return true
  }

  async _setMenuState() {
    try {
      /** Wait for next paint to avoid flicker / ensure layout stabilizes.
       *  Using requestAnimationFrame is more appropriate for render-related
       *  waiting than setTimeout. We also await a microtask to further
       *  ensure styles/layout are applied.
       */
      await this._waitForLayoutSettled()

      /** Make nav and ul invisible to avoid flickering */
      this.element.classList.add(this.invisibleClass)
      this.contentTarget.classList.add(this.invisibleClass)

      /** Remove the hidden class to show the menu */
      this.contentTarget.classList.remove(this.hiddenClass)

      /** Hide toggler to calculate the menu width */
      this._hideToggler()

      /** Expand menu to take measurements */
      this._expandMenu()

      /** Calculate the width of the menu and container */
      const menuWidth = this._calculateMenuWidth()
      const logoWidth = this._calculateLogoWidth()
      const dropdownWidth = this._calculateDropdownWidth()
      const contentWidth = this._calculateContainerWidth() - logoWidth - dropdownWidth

      if (menuWidth === 0 || contentWidth === 0) return

      /** Toggler height = menu height */
      this.togglerHeight = this._calculateTogglerHeight()

      /** Determine the state of the menu (synchronous) */
      const state = this._determineMenuState(contentWidth, menuWidth)

      /** Finally set the menu properties based on the state,
       *  wait for final state with Promise.
       */
      await this._finallySetMenuProperties(state)

      /** Adjust css classes based on the state */
      this._setMenuLogoContainerClass(state)

      /** Reveal menu */
      this.element.classList.remove(this.invisibleClass)
      this.contentTarget.classList.remove(this.invisibleClass)

      /** Remember the decision so subsequent renders (Turbo navigation, table
       *  sticky-header changes, form validation re-render) can apply it
       *  synchronously instead of repeating the hide/measure/reveal dance. */
      cachedDecision = {
        viewportWidth: window.innerWidth,
        state,
        togglerHeight: this.togglerHeight,
      }
    } catch (err) {
      console.error("ResponsiveMenuController: error in _setMenuState", err)
    }
  }

  /** Finally set the menu properties based on the state.
   *  Use Promise to wait for final layout changes.
   */
  _finallySetMenuProperties(state) {
    return (async () => {
      if (state === "collapsed") {
        this._collapseMenu()
        this._showToggler()
        this._hideMenu()
      } else {
        this._expandMenu()
        this._hideToggler()
        this._showMenu()
        this._removeClickOutsideListener()
      }

      await this._waitForLayoutSettled()
    })()
  }

  /** microtask tick to help stabilize computed styles/layout */
  async _waitForLayoutSettled() {
    await new Promise((resolve) => requestAnimationFrame(resolve))
    await Promise.resolve()
  }

  /** Determine the state of the menu based
   *  on the width of the container.
   *  Synchronous — this is a pure computation.
   */
  _determineMenuState(contentWidth = 0, menuWidth = 0) {
    if (contentWidth - menuWidth < 0) {
      return "collapsed"
    } else {
      return "expanded"
    }
  }

  /** When the hamburger menu is displayed,
   *  move it to the end of the element.
   */
  _setMenuLogoContainerClass(state) {
    if (this.hasMenuLogoContainerTarget) {
      if (state === "expanded") {
        this.menuLogoContainerTarget.classList.remove("justify-between")
        this.menuLogoContainerTarget.classList.add("justify-start")
      } else {
        this.menuLogoContainerTarget.classList.remove("justify-start")
        this.menuLogoContainerTarget.classList.add("justify-between")
      }
    }
  }

  _calculateContainerWidth() {
    return this.element.offsetWidth
  }

  _calculateMenuWidth() {
    return this.contentTarget.offsetWidth
  }

  _calculateLogoWidth() {
    return this.hasLogoTarget ? this.logoTarget.offsetWidth : 0
  }

  _calculateDropdownWidth() {
    return this.hasDropdownTarget ? this.dropdownTarget.offsetWidth : 0
  }

  _calculateTogglerHeight() {
    return this.contentTarget.offsetHeight
  }

  _expandMenu() {
    this.element.classList.remove(this.collapsedClass)
    this.element.classList.add(this.expandedClass)
  }

  _collapseMenu() {
    this.element.classList.remove(this.expandedClass)
    this.element.classList.add(this.collapsedClass)
  }

  _showMenu() {
    this.togglerTarget.ariaExpanded = true
    this.contentTarget.classList.remove(this.hiddenClass)
  }

  _hideMenu() {
    this.togglerTarget.ariaExpanded = false
    this.contentTarget.classList.add(this.hiddenClass)
  }

  _showToggler() {
    this.togglerTarget.removeAttribute("hidden")
    this.togglerTarget.classList.remove(this.hiddenClass)
    this.togglerTarget.style.height = this.togglerHeight + "px"
    this._calculateMenuPosition(false)
  }

  _hideToggler() {
    this.togglerTarget.classList.add(this.hiddenClass)
    this._calculateMenuPosition()
  }

  _calculateMenuPosition(expanded = true) {
    if (expanded) {
      /* Remove menu position */
      this.contentTarget.style.right = 0
      this.contentTarget.style.top = 0
    } else {
      setTimeout(() => {
        /* Calculate menu position for collapsed state */
        let top = this.togglerTarget.offsetTop + this.togglerTarget.offsetHeight
        const right =
          window.innerWidth -
          this.togglerTarget.getBoundingClientRect().right +
          parseFloat(window.getComputedStyle(this.togglerTarget).paddingRight)
        if (this.hasDropdownTarget) {
          top -= this.dropdownTarget.offsetHeight / 2
        }

        this.contentTarget.style.right = `${right}px`
        this.contentTarget.style.top = `${top}px`
        this.contentTarget.classList.add(this.hiddenClass)
      })
    }
  }

  _addClickOutsideListener() {
    document.addEventListener("click", this.clickOutsideListener)
  }

  _removeClickOutsideListener() {
    document.removeEventListener("click", this.clickOutsideListener)
  }
}
